import React, { useState, useEffect, useCallback } from 'react';
import reklamacjeService from '../../services/reklamacjeService';

/**
 * Wybor adresatow reklamacji przed wysylka.
 * - lista adresatow ze slownika (admin zarzadza nia w panelu) jako checkboxy
 * - pole wolnego tekstu dla adresu spoza slownika (mozna kilka po przecinku)
 *
 * UI standard V3SYS: font 12px/11px/10px, padding 6px 12px / 4px 10px,
 * wylacznie tokeny kolorow var(--...).
 */
const WyslijEmailModal = ({ isOpen, onClose, onWyslij, sugerowany }) => {
  const [adresaci, setAdresaci] = useState([]);
  const [zaznaczone, setZaznaczone] = useState(new Set());
  const [wlasny, setWlasny] = useState('');
  const [loading, setLoading] = useState(false);
  const [wysylanie, setWysylanie] = useState(false);

  const wczytaj = useCallback(async () => {
    setLoading(true);
    try {
      const res = await reklamacjeService.getAdresaci();
      const lista = res?.data || res || [];
      setAdresaci(Array.isArray(lista) ? lista : []);
      // domyslni adresaci zaznaczaja sie sami
      setZaznaczone(new Set(
        (Array.isArray(lista) ? lista : []).filter(a => a.domyslny).map(a => a.email)
      ));
    } catch (e) {
      setAdresaci([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setWlasny(sugerowany || '');
    wczytaj();
  }, [isOpen, sugerowany, wczytaj]);

  if (!isOpen) return null;

  const przelacz = (email) => {
    setZaznaczone(prev => {
      const n = new Set(prev);
      if (n.has(email)) n.delete(email); else n.add(email);
      return n;
    });
  };

  // Adresy z pola wolnego tekstu: dopuszczamy kilka po przecinku lub sredniku
  const wlasneAdresy = wlasny
    .split(/[,;]/)
    .map(x => x.trim())
    .filter(x => x.length > 0);

  const wszystkie = Array.from(new Set([...zaznaczone, ...wlasneAdresy]));
  const bledny = wlasneAdresy.find(a => !a.includes('@'));
  const moznaWyslac = wszystkie.length > 0 && !bledny && !wysylanie;

  const wyslij = async () => {
    if (!moznaWyslac) return;
    setWysylanie(true);
    try {
      await onWyslij(wszystkie);
    } finally {
      setWysylanie(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '95%', maxWidth: '520px',
        maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Naglowek */}
        <div style={{
          padding: '12px 14px', borderBottom: '1px solid var(--border)', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 600 }}>Wyślij reklamację e-mailem</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Zaznacz adresatów albo wpisz adres ręcznie
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '20px', color: 'var(--text-muted)', padding: '0 4px'
          }}>×</button>
        </div>

        {/* Lista adresatow */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 14px' }}>
          {loading ? (
            <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Ładowanie listy...</div>
          ) : adresaci.length === 0 ? (
            <div style={{
              fontSize: '11px', color: 'var(--text-muted)', backgroundColor: 'var(--bg)',
              borderRadius: '4px', padding: '8px 10px', marginBottom: '10px'
            }}>
              Lista adresatów jest pusta. Administrator może ją uzupełnić w panelu:
              <strong> Admin → Firma → Maile reklamacji</strong>. Na razie wpisz adres poniżej.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' }}>
              {adresaci.map(a => {
                const wybrany = zaznaczone.has(a.email);
                return (
                  <label key={a.id} style={{
                    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
                    padding: '6px 8px', borderRadius: '4px',
                    border: `1px solid ${wybrany ? 'var(--success)' : 'var(--border)'}`,
                    backgroundColor: wybrany ? 'var(--success-light)' : 'var(--card-bg)'
                  }}>
                    <input
                      type="checkbox"
                      checked={wybrany}
                      onChange={() => przelacz(a.email)}
                      style={{ width: '14px', height: '14px', cursor: 'pointer' }}
                    />
                    <span style={{ flex: 1 }}>
                      <span style={{ fontSize: '12px', fontWeight: 600 }}>{a.nazwa}</span>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginLeft: '6px' }}>
                        {a.email}
                      </span>
                      {a.opis && (
                        <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)' }}>
                          {a.opis}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          <div>
            <label style={{
              display: 'block', fontSize: '11px', fontWeight: 600,
              color: 'var(--text-secondary)', marginBottom: '4px'
            }}>
              Inny adres (jeśli nie ma go na liście)
            </label>
            <input
              type="text"
              value={wlasny}
              onChange={(e) => setWlasny(e.target.value)}
              placeholder="reklamacje@dostawca.pl"
              style={{
                width: '100%', padding: '6px 10px', fontSize: '12px',
                border: `1px solid ${bledny ? 'var(--danger)' : 'var(--border)'}`,
                borderRadius: '4px', backgroundColor: 'var(--card-bg)', color: 'var(--text)'
              }}
            />
            <div style={{ fontSize: '10px', color: bledny ? 'var(--danger)' : 'var(--text-muted)', marginTop: '3px' }}>
              {bledny
                ? `Adres "${bledny}" wygląda na niepoprawny (brak @).`
                : 'Kilka adresów oddziel przecinkiem.'}
            </div>
          </div>
        </div>

        {/* Stopka */}
        <div style={{
          padding: '10px 14px', borderTop: '1px solid var(--border)', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          backgroundColor: 'var(--bg)'
        }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {wszystkie.length > 0
              ? `Wyślij do ${wszystkie.length} ${wszystkie.length === 1 ? 'adresata' : 'adresatów'}`
              : 'Nie wybrano adresata'}
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button onClick={onClose} style={{
              padding: '6px 12px', fontSize: '12px', cursor: 'pointer',
              border: '1px solid var(--border)', borderRadius: '4px',
              backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)'
            }}>Anuluj</button>
            <button onClick={wyslij} disabled={!moznaWyslac} style={{
              padding: '6px 12px', fontSize: '12px', fontWeight: 600, borderRadius: '4px',
              cursor: moznaWyslac ? 'pointer' : 'not-allowed',
              border: `1px solid ${moznaWyslac ? 'var(--success)' : 'var(--border)'}`,
              backgroundColor: moznaWyslac ? 'var(--success)' : 'var(--border)',
              color: moznaWyslac ? '#fff' : 'var(--text-muted)'
            }}>
              {wysylanie ? 'Wysyłanie...' : 'Wyślij'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WyslijEmailModal;
